// math.js — life-expectancy projections for the shock & hope screens.
// Formula parity with Opal-OS/legacy-opal-ios:
//   app/statistics/ScreentimeProjections.swift

export const AGE_BUCKETS = {
  '18-24': { mid: 21, remaining: 58 },
  '25-34': { mid: 30, remaining: 50 },
  '35-44': { mid: 40, remaining: 41 },
  '45-54': { mid: 50, remaining: 31 },
  '55-65': { mid: 60, remaining: 22 },
  // /2026-start uses a 55-64 boundary (vs /detox's 55-65); same midpoint/remaining
  // so the time projection is consistent across both granular age splits.
  '55-64': { mid: 60, remaining: 22 },
  '65+':   { mid: 70, remaining: 14 },
  // Legacy bucket — kept for backwards compatibility with stored state from
  // before the 45+/55-65/65+ split. Same remaining-years as the original
  // bucket so existing projection tests don't drift.
  '45+':   { mid: 52, remaining: 30 },
};

// iOS constants (ScreentimeProjections.swift)
export const AWAKE_HOURS       = 16;
export const HOURS_IN_DAY      = 24;
export const DAYS_IN_YEAR      = 365;
export const AVERAGE_LIFESPAN  = 72;
export const OPAL_SAVING_RATIO = 0.3;

// Hybrid framing: iOS 24/16 waking-hours scaling × remaining-years per bucket.
// = dailyHours × remaining / 16 — honors age input while inheriting iOS shock.
export function calcScreenYears(dailyHours, ageBucket) {
  if (!dailyHours) return 0;
  const rem = AGE_BUCKETS[ageBucket]?.remaining ?? 50;
  const y = (dailyHours * rem) / AWAKE_HOURS;
  return Math.round(y * 10) / 10;
}

export function calcReclaimedYears(dailyHours, ageBucket) {
  return Math.round(calcScreenYears(dailyHours, ageBucket) * OPAL_SAVING_RATIO * 10) / 10;
}

// iOS yearly-days headline ("X days on your phone this year").
// Age-agnostic; uses iOS 24/16 scaling.
export function calcYearlyDays(dailyHours) {
  if (!dailyHours) return 0;
  return (dailyHours * DAYS_IN_YEAR) / AWAKE_HOURS;
}

// iOS "target daily" copy: reduce by 30% → "3h 30m".
export function calcAdjustedDailyHours(dailyHours) {
  return dailyHours * (1 - OPAL_SAVING_RATIO);
}

// iOS DateComponentsFormatter with .full style truncates to the whole unit
// (.dropAll on zero). These match that semantics — Math.floor, not round.
export function formatYears(years) {
  const n = Math.floor(years);
  return `${n} ${n === 1 ? 'year' : 'years'}`;
}

export function formatDays(days) {
  const n = Math.floor(days);
  return `${n} ${n === 1 ? 'day' : 'days'}`;
}

export function formatHoursMinutes(totalHours) {
  // Compute via total-minutes to avoid float drift (8×0.7 → 35m instead of 36m).
  const totalMinutes = Math.round(totalHours * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes - h * 60;
  const parts = [];
  if (h > 0) parts.push(`${h}h`);
  if (m > 0) parts.push(`${m}m`);
  return parts.length ? parts.join(' ') : '0m';
}

export function screenTimeBucket(dailyHours) {
  if (dailyHours <= 2) return '0-2h';
  if (dailyHours <= 4) return '2-4h';
  if (dailyHours <= 6) return '4-6h';
  if (dailyHours <= 8) return '6-8h';
  return '8h+';
}

// User-facing detox tiers — the Light/Moderate/Heavy/Very Heavy framing from
// the Meta ad. Cutoffs: 1–2 / 3–4 / 5–6 / 7+. Fractional hours round down
// into the lower tier (e.g., 2.5 → Light, 4.9 → Moderate).
export const DETOX_TIERS = {
  light:      { id: 'light',      label: 'Light',      range: '1–2h/day' },
  moderate:   { id: 'moderate',   label: 'Moderate',   range: '3–4h/day' },
  heavy:      { id: 'heavy',      label: 'Heavy',      range: '5–6h/day' },
  very_heavy: { id: 'very_heavy', label: 'Very Heavy', range: '7+ h/day' },
};

export function detoxTier(dailyHours) {
  const h = Number(dailyHours);
  if (!Number.isFinite(h) || h <= 0) return DETOX_TIERS.light;
  if (h < 3) return DETOX_TIERS.light;
  if (h < 5) return DETOX_TIERS.moderate;
  if (h < 7) return DETOX_TIERS.heavy;
  return DETOX_TIERS.very_heavy;
}

if (typeof window !== 'undefined') {
  Object.assign(window, {
    AGE_BUCKETS,
    calcScreenYears, calcReclaimedYears,
    calcYearlyDays, calcAdjustedDailyHours,
    formatYears, formatDays, formatHoursMinutes,
    screenTimeBucket,
    DETOX_TIERS, detoxTier,
  });
}
